// The book arrives in two halves, and this puts them back together.
//
// The server encodes the base book once per rebuild (`GET /api/book/base`, gzip, ETag) and
// answers `GET /api/book` with only what changes on top of it: the live overlays (saved
// contacts, research, why-now lines, approved picks...) and the caller's own blocks (viewer,
// board_prefs, bank_requests). Encoding the whole book on every load is what OOM-killed the
// service on 12-14 Oct. See book_cache.py, whose `merge_book_delta` is the reference for this
// function; both are tested against the same fixture (bookDelta.fixture.json).
//
// The result is exactly the payload `/api/book` used to send whole, so nothing downstream of
// `fetchBook` knows the difference.

import type { BookAccount, BookPayload } from './types.ts';

// One overlaid row: the fields that are new on it, and any the overlay took away.
export interface BookAccountPatch {
  index: number;
  id: string | null;
  set: Partial<BookAccount> & Record<string, unknown>;
  unset: string[];
}

export interface BookDelta {
  // Which encoded base this delta was cut from. The base request names it, and a 409 means a
  // rebuild landed between the two requests.
  base_version: string;
  top_level: Partial<BookPayload> & Record<string, unknown>;
  removed_top_level: string[];
  account_patches: BookAccountPatch[];
  appended_accounts: BookAccount[];
  // Set only when an overlay moved or dropped base rows: the whole list, replacing the base's.
  accounts: BookAccount[] | null;
}

/** The base plus the delta: the payload the old `/api/book` sent. Never mutates either. */
export function mergeBookDelta(base: BookPayload, delta: Partial<BookDelta>): BookPayload {
  // Top-level keys: the delta's win, and any it removed go.
  const merged: Record<string, unknown> = { ...base, ...(delta.top_level ?? {}) };
  for (const key of delta.removed_top_level ?? []) delete merged[key];

  // A whole replacement list wins outright.
  if (delta.accounts) {
    merged.accounts = [...delta.accounts];
    return merged as unknown as BookPayload;
  }

  // Nothing changed on the rows: the base list stands as it is.
  const patches = delta.account_patches ?? [];
  const appended = delta.appended_accounts ?? [];
  if (patches.length === 0 && appended.length === 0) return merged as unknown as BookPayload;

  // Patch replaced rows by index, then append the overlays' new rows.
  const accounts: BookAccount[] = [...(base.accounts ?? [])];
  for (const patch of patches) {
    const row: Record<string, unknown> = { ...accounts[patch.index], ...patch.set };
    for (const key of patch.unset ?? []) delete row[key];
    accounts[patch.index] = row as unknown as BookAccount;
  }
  accounts.push(...appended);
  merged.accounts = accounts;
  return merged as unknown as BookPayload;
}
